import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatIntervalo, formatPoints, type LinhaDaTemporada } from "@nhomi/core";
import { watchChallenge, type Challenge } from "@/data/challenges";
import { watchTemporada, type Temporada as TemporadaDoc } from "@/data/temporadas";
import { Empty, Spinner } from "@/ui/primitives";
import { IconBack } from "@/ui/icons";

/* ============================================================
   O pódio de uma semana

   Mostra quem ganhou a semana e gera a IMAGEM para compartilhar — é o
   que vai para o grupo do WhatsApp e, de quebra, divulga o app. A imagem
   é desenhada num canvas aqui mesmo (1080×1350, o formato que o WhatsApp
   e o Instagram mostram inteiro), com as iniciais no lugar das fotos: a
   foto do Storage não pode ser desenhada num canvas que depois vira
   arquivo sem liberar CORS no bucket, e isso não vale o risco.
   ============================================================ */

const CORES = ["#C8553D", "#3D6BC8", "#8E5BC8", "#D99A1E", "#2F8F83", "#B5487A"];
const corDe = (uid: string) => CORES[[...uid].reduce((a, c) => a + c.charCodeAt(0), 0) % CORES.length]!;
const iniciais = (nome: string) => {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  return (p.length > 1 ? p[0]![0]! + p[p.length - 1]![0]! : p[0]?.[0] ?? "?").toUpperCase();
};

export interface Podio {
  desafio: string;
  /** "5 a 11 de outubro" · "setembro" · "2026". */
  intervalo: string;
  /** "Ana ganhou a semana 🏆". */
  frase: string;
  placar: LinhaDaTemporada[];
  campeoes: string[];
}

/** A frase do topo: "Ana ganhou a semana", "Ana e Bia ganharam o mês". */
export function fraseDoPodio(p: Pick<Podio, "placar" | "campeoes">, oQue: string): string {
  const campeoes = p.placar.filter((l) => p.campeoes.includes(l.uid));
  const nomes = campeoes.map((c) => c.name.split(" ")[0]).join(" e ") || "Ninguém";
  return `${nomes} ${campeoes.length > 1 ? "ganharam" : "ganhou"} ${oQue} 🏆`;
}

export async function desenharPodio(t: Podio): Promise<Blob> {
  await document.fonts?.load?.("800 64px Nunito").catch(() => undefined);
  const W = 1080, H = 1350;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d")!;
  const fundo = g.createLinearGradient(0, 0, W * 0.4, H);
  fundo.addColorStop(0, "#5E8F37"); fundo.addColorStop(0.65, "#3F6224"); fundo.addColorStop(1, "#2C4A18");
  g.fillStyle = fundo; g.fillRect(0, 0, W, H);
  const fonte = (peso: number, tam: number) => `${peso} ${tam}px Nunito, system-ui, sans-serif`;

  // marca
  g.fillStyle = "#fff"; roundRect(g, 80, 80, 76, 76, 20); g.fill();
  g.fillStyle = "#4F7A2E"; g.font = fonte(800, 52); g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText("n", 118, 120);
  g.fillStyle = "#fff"; g.textAlign = "left"; g.font = fonte(800, 54); g.fillText("nhomi", 178, 120);

  // título
  g.globalAlpha = 0.85; g.font = fonte(700, 40); g.textBaseline = "alphabetic";
  g.fillText(`${t.desafio} · ${t.intervalo}`, 80, 290);
  g.globalAlpha = 1; g.font = fonte(800, 92);
  quebrar(g, t.frase, 80, 400, W - 160, 104);

  // pódio: 2º, 1º, 3º
  const top = [t.placar[1], t.placar[0], t.placar[2]];
  const alturas = [210, 310, 140];
  const cores = ["rgba(255,255,255,.22)", "#F0C419", "rgba(255,255,255,.22)"];
  const rotulos = ["2º", "1º", "3º"];
  const larg = 250, gap = 30, base = 1180;
  const x0 = (W - (larg * 3 + gap * 2)) / 2;
  top.forEach((l, i) => {
    if (!l) return;
    const x = x0 + i * (larg + gap), h = alturas[i]!;
    g.fillStyle = cores[i]!; roundRect(g, x, base - h, larg, h, 22, true); g.fill();
    g.fillStyle = i === 1 ? "#4a3a00" : "#fff"; g.font = fonte(800, 54); g.textAlign = "center";
    g.fillText(rotulos[i]!, x + larg / 2, base - h + 80);
    // avatar
    const cy = base - h - 190;
    g.fillStyle = corDe(l.uid); g.beginPath(); g.arc(x + larg / 2, cy, 62, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff"; g.font = fonte(800, 50); g.textBaseline = "middle";
    g.fillText(iniciais(l.name), x + larg / 2, cy + 2);
    if (t.campeoes.includes(l.uid)) { g.font = fonte(400, 60); g.fillText("👑", x + larg / 2, cy - 82); }
    g.textBaseline = "alphabetic"; g.font = fonte(800, 40);
    g.fillText(l.name.split(" ")[0]!.slice(0, 12), x + larg / 2, cy + 120);
    g.globalAlpha = 0.85; g.font = fonte(700, 34);
    g.fillText(`${formatPoints(l.pontos)} pts`, x + larg / 2, cy + 165);
    g.globalAlpha = 1;
  });

  g.textAlign = "center"; g.font = fonte(800, 38); g.globalAlpha = 0.92;
  g.fillText("Monta o seu grupo em nhomi.app", W / 2, H - 70);
  return new Promise((ok, erro) => c.toBlob((b) => (b ? ok(b) : erro(new Error("sem imagem"))), "image/png"));
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, soCima = false) {
  g.beginPath();
  g.moveTo(x + r, y); g.lineTo(x + w - r, y); g.quadraticCurveTo(x + w, y, x + w, y + r);
  if (soCima) { g.lineTo(x + w, y + h); g.lineTo(x, y + h); }
  else { g.lineTo(x + w, y + h - r); g.quadraticCurveTo(x + w, y + h, x + w - r, y + h); g.lineTo(x + r, y + h); g.quadraticCurveTo(x, y + h, x, y + h - r); }
  g.lineTo(x, y + r); g.quadraticCurveTo(x, y, x + r, y); g.closePath();
}

function quebrar(g: CanvasRenderingContext2D, texto: string, x: number, y: number, max: number, linha: number) {
  const palavras = texto.split(" ");
  let atual = "";
  for (const p of palavras) {
    const teste = atual ? `${atual} ${p}` : p;
    if (g.measureText(teste).width > max && atual) { g.fillText(atual, x, y); y += linha; atual = p; }
    else atual = teste;
  }
  if (atual) g.fillText(atual, x, y);
}

export function Temporada() {
  const { cid = "", de = "" } = useParams();
  const [desafio, setDesafio] = useState<Challenge | null>(null);
  const [t, setT] = useState<TemporadaDoc | null | undefined>(undefined);
  const [img, setImg] = useState<{ url: string; blob: Blob } | null>(null);
  const [aviso, setAviso] = useState("");
  const urlRef = useRef("");

  useEffect(() => watchChallenge(cid, setDesafio), [cid]);
  useEffect(() => watchTemporada(cid, de, setT), [cid, de]);

  useEffect(() => {
    if (!t || !desafio) return;
    let vivo = true;
    void desenharPodio({
      desafio: desafio.name, intervalo: formatIntervalo(t.de, t.ate, new Date().getFullYear()),
      frase: fraseDoPodio(t, "a semana"), placar: t.placar, campeoes: t.campeoes,
    }).then((blob) => {
      if (!vivo) return;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      setImg({ url: urlRef.current, blob });
    });
    return () => { vivo = false; };
  }, [t, desafio]);
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  async function compartilhar() {
    if (!img) return;
    const arquivo = new File([img.blob], `nhomi-podio-${de}.png`, { type: "image/png" });
    const texto = `🏆 ${desafio?.name ?? "Nhomi"}: o pódio da semana. https://nhomi.app`;
    try {
      if (navigator.canShare?.({ files: [arquivo] })) {
        await navigator.share({ files: [arquivo], text: texto });
        return;
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
    }
    // Sem compartilhar arquivo (computador): baixa a imagem.
    const a = document.createElement("a");
    a.href = img.url; a.download = arquivo.name; a.click();
    setAviso("Imagem baixada. É só mandar no grupo.");
  }

  if (t === undefined) return <div className="screen center"><Spinner /></div>;
  if (!t) {
    return (
      <main className="screen">
        <Empty emoji="🏆" title="Essa semana ainda não fechou">
          <p>O pódio sai toda segunda, logo depois da meia-noite.</p>
        </Empty>
      </main>
    );
  }

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}/classificacao`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title">Pódio da semana</div>
        <span className="topbar-btn" />
      </div>
      <div className="screen-body pad">
        {img ? <img className="podio-img" src={img.url} alt="O pódio da semana" /> : <Spinner />}
        <button className="btn btn-primary" onClick={() => void compartilhar()} disabled={!img}>
          Compartilhar no WhatsApp
        </button>
        {aviso && <p className="hint-row center">{aviso}</p>}
        <div className="section-label left">Placar da semana</div>
        <div className="card lista-crua">
          {t.placar.map((l: LinhaDaTemporada, i) => (
            <Link key={l.uid} className="rank-row" to={`/c/${cid}/u/${l.uid}`}>
              <div className="rank-main">
                <div className="rank-name">{i + 1}º {l.name}{t.campeoes.includes(l.uid) ? " 👑" : ""}</div>
                <div className="rank-sub">{l.dias} {l.dias === 1 ? "dia ativo" : "dias ativos"}</div>
              </div>
              <div className="rank-pos">{formatPoints(l.pontos)}</div>
            </Link>
          ))}
        </div>
        <div className="gap" />
      </div>
    </main>
  );
}
